/**
 * Session state.
 *
 * The session itself lives in an `HttpOnly` cookie that JavaScript cannot read,
 * so nothing about the credential is kept here. The store only caches the last
 * account the server reported, which lets the header render without a flash
 * while `GET /api/auth/me` is still in flight.
 */

import { create } from 'zustand'

import type { UserDto } from '@shared/types'

interface SessionState {
  /** Last account confirmed by the server, `null` when signed out. */
  user: UserDto | null
  /** True until the boot-time `/api/auth/me` check has finished. */
  hydrating: boolean
  setUser: (user: UserDto | null) => void
  /**
   * Ends hydration without an answer.
   *
   * Used when the probe failed for a reason other than a missing session: the
   * account is still unknown, but waiting can be over, so a guard can render the
   * failure and offer a retry instead of a spinner that never stops.
   */
  endHydration: () => void
  clear: () => void
}

export const useSessionStore = create<SessionState>((set) => ({
  user: null,
  hydrating: true,
  setUser: (user) => set({ user, hydrating: false }),
  endHydration: () => set({ hydrating: false }),
  clear: () => set({ user: null, hydrating: false }),
}))

/**
 * Whether a session was confirmed on this page load.
 *
 * Read through a selector so a hook can gate its own request on it without
 * subscribing to the whole store.
 */
export function selectIsAuthenticated(state: SessionState): boolean {
  return state.user !== null
}
